import type { ReportVisibility } from "../types/types.ts"
import type { ShareEditorKind } from "./reportSharing.ts"

const OFFERED_VISIBILITIES: readonly ReportVisibility[] = [
  "private",
  "role",
  "company"
]

const TARGET_KINDS: Record<ReportVisibility, readonly ShareEditorKind[]> = {
  private: ["user"],
  team: ["user", "role"],
  role: ["role"],
  company: ["user"]
}

/** The kinds of target a share picker may offer for a report.
 *
 * `company` offers users because company visibility grants reading only: an
 * explicit share is the only way to name an editor. The fallback is reachable
 * despite the type: the union is mirrored from the backend by hand, so a server
 * that has gained a fifth value can send one this build does not know.
 */
export const shareTargetKinds = (
  visibility: ReportVisibility
): readonly ShareEditorKind[] => TARGET_KINDS[visibility] ?? TARGET_KINDS.team

/** The visibilities a chooser offers for a report currently saved as `saved`.
 *
 * A retired value is appended so the report is not misrepresented: antd renders
 * a value absent from its options as the raw string, or as nothing selected.
 * It takes the **saved** value rather than the live selection, or the option
 * would vanish the moment the user looked at another one.
 */
export const visibilityChoicesFor = (
  saved: ReportVisibility
): ReportVisibility[] =>
  OFFERED_VISIBILITIES.includes(saved)
    ? [...OFFERED_VISIBILITIES]
    : [...OFFERED_VISIBILITIES, saved]

/** Whether a visibility reaches people the report was never shared with.
 *
 * Mirrors the two visibility-keyed clauses of the backend's own read predicate;
 * keep the set in step with them.
 */
export const reachesBeyondItsShares = (visibility: ReportVisibility): boolean =>
  visibility === "team" || visibility === "company"
